import axios, { AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios'
import { BillableDeliveryNotePatchRequest, BillableDeliveryNoteResponse } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.dto'
import { IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { CommonFilterModel, CommonParams, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { ReportGridRequest } from '@/api/services/common.dto'
import { BillingCaseSearchGridRequest, BillingPatientSearchGridRequest } from '@/api/services/billing/billing.dto'
import { Nullable } from '@/shared/types/app.types'
import { EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

class BillingCommonDeliveryNoteService {
    async get(
        laboratoryId: number,
        billableDeliveryNoteId: number,
        cancelToken?: CancelToken
    ): Promise<BillableDeliveryNoteResponse> {
        const response =
            await axios.get<BillableDeliveryNoteResponse>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes/${billableDeliveryNoteId}`,
                { cancelToken })
        return response.data
    }

    async paginator(
        filterModel: Nullable<CommonFilterModel>,
        params: Nullable<CommonParams>,
        laboratoryId: number
    ): Promise<IResponseAgGrid<BillableDeliveryNoteResponse>> {
        const response =
            await axios.put<IResponseAgGrid<BillableDeliveryNoteResponse>>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes`,
                { ...filterModel },
                { params })
        return response.data
    }
    async export(
        laboratoryId: number,
        gridColumns?: ReportGridRequest,
        params?: ExportCommonParams
    ): Promise<AxiosResponse<Blob>> {
        return axios.put<Blob>(
            `/billing/laboratories/${laboratoryId}/billableDeliveryNotes/export`,
            { ...gridColumns },
            { params, responseType: 'arraybuffer' })
    }

    async searchPatient(
        laboratoryId: number,
        request: BillingPatientSearchGridRequest,
        params?: CommonParams
    ): Promise<IResponseAgGrid<BillableDeliveryNoteResponse>> {
        const response =
            await axios.put<IResponseAgGrid<BillableDeliveryNoteResponse>>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes/patient`,
                request,
                { params })
        return response.data
    }
    async searchCase(
        laboratoryId: number,
        request: BillingCaseSearchGridRequest,
        params?: CommonParams
    ): Promise<IResponseAgGrid<BillableDeliveryNoteResponse>> {
        const response =
            await axios.put<IResponseAgGrid<BillableDeliveryNoteResponse>>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes/case`,
                request,
                { params })
        return response.data
    }

    async getReport(
        laboratoryId: number,
        billableDeliveryNoteId: number,
        encoded: boolean
    ): Promise<Blob> {
        const config: AxiosRequestConfig = {
            params: { encoded },
            headers: { type: 'application/pdf' },
            responseType: encoded ? 'text' : 'arraybuffer'
        }
        const response =
            await axios.put<Blob>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes/${billableDeliveryNoteId}/report`,
                undefined,
                config)
        return response.data
    }

    async create(
        laboratoryId: number,
        appliedBillableConceptIds: number[]
    ): Promise<BillableDeliveryNoteResponse> {
        const response =
            await axios.post<BillableDeliveryNoteResponse>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes`,
                appliedBillableConceptIds)
        return response.data
    }

    async patch(
        laboratoryId: number,
        request: BillableDeliveryNotePatchRequest
    ): Promise<EntityIdentifierWithErrorResponse[]> {
        const response =
            await axios.patch<EntityIdentifierWithErrorResponse[]>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes`,
                request)
        return response.data
    }

    async delete(
        laboratoryId: number,
        billableDeliveryNoteId: number
    ): Promise<void> {
        const response =
            await axios.delete<void>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes/${billableDeliveryNoteId}`)
        return response.data
    }

    async addConcepts(
        laboratoryId: number,
        billableDeliveryNoteId: number,
        appliedBillableConceptIds: number[]
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes/${billableDeliveryNoteId}/addConcepts`,
                appliedBillableConceptIds)
        return response.data
    }
    async removeConcepts(
        laboratoryId: number,
        billableDeliveryNoteId: number,
        appliedBillableConceptIds: number[]
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes/${billableDeliveryNoteId}/removeConcepts`,
                appliedBillableConceptIds)
        return response.data
    }

    async markAsVerified(
        laboratoryId: number,
        billableDeliveryNoteId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes/${billableDeliveryNoteId}/markAsVerified`)
        return response.data
    }
    async markAsIssued(
        laboratoryId: number,
        billableDeliveryNoteId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/billableDeliveryNotes/${billableDeliveryNoteId}/markAsIssued`)
        return response.data
    }
}
export const billingCommonDeliveryNoteService = new BillingCommonDeliveryNoteService()
